import React, { useState, useCallback, useEffect,useMemo } from 'react';
import { StyleSheet, View, BackHandler, Modal } from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import PropTypes from 'prop-types';
import { ConfirmationModalContext } from 'LocalGluons/components/ConfirmationModal/ConfirmationModalContext';
import { isTokenError } from 'Utils/mCoreUtils';
import { getErrorMessage } from 'Utils/index';
import { Device, getOrientation, lockToLandscape } from 'DeviceUtils';
import { AppConfirmationModal, AppPinInputModal } from './modals';

const AppModal = ({ children }) => {
    const [modalContent, setModalContent] = useState(null);
    const [customBackHandler, setCustomBackHandler] = useState(undefined);

    useFocusEffect(
        useCallback(() => {
            const onBackPress = () => {
                if (modalContent) {
                    if (modalContent?.platform === undefined) {
                        return false;
                    }
                    return true;
                }
                return false;
            };
            const subscription = BackHandler.addEventListener('hardwareBackPress', onBackPress);

            return () => subscription.remove();
        }, [modalContent])
    );

    const closeModalCallback = useCallback(() => {
        if (customBackHandler == null) {
            setModalContent(null);
        } else if (customBackHandler) {
            customBackHandler();
            setCustomBackHandler(null);
        }
    }, [customBackHandler]);

    const setErrorModalCallback = useCallback(async (errorResponse, callback) => {
        if (errorResponse && !isTokenError(errorResponse)) {
            const orientation = await getOrientation();
            const isLandscape = orientation.includes('LANDSCAPE');

            setModalContent(
                <AppConfirmationModal
                    errorMessage={{ message: getErrorMessage(errorResponse) }}
                    closeCallback={() => {
                        if (typeof callback === 'function') callback();
                        closeModalCallback();
                    }}
                    mode={isLandscape ? 'landscape' : 'portrait'}
                />
            );
        }
    }, []);

    const setProtectedContentCallback = useCallback(
        (
            protectedContent,
            successCallback,
            fromPlayer,
            closeCallback = closeModalCallback,
            forgotPinCallback,
            opaque = false
        ) => {
            if (protectedContent) {
                setModalContent(
                    <AppPinInputModal
                        protectedContent={protectedContent}
                        successCallback={(props) => {
                            if (Device.isTablet) lockToLandscape();
                            successCallback(props);
                        }}
                        fromPlayer={fromPlayer}
                        closeCallback={(props) => {
                            if (Device.isTablet) lockToLandscape();
                            closeCallback(props);
                        }}
                        forgotPinCallback={forgotPinCallback}
                        opaque={opaque}
                    />
                );
            }
        },
        []
    );

    const setContentHandler = useCallback((newModalContent, hwBackHandler) => {
        setCustomBackHandler(() => hwBackHandler);
        setModalContent(newModalContent);
    }, []);

    const modalSetup = useMemo(() => {
        // console.log('checkFunction2');
        return {
            setErrorModal: setErrorModalCallback,
            setProtectedContent: setProtectedContentCallback,
            modalContent,
            setModalContent: setContentHandler
        };
    }, []);

    if (Device.isWeb) {
        // console.log('Ahooj', modalContent);
        return (
            <ConfirmationModalContext.Provider value={modalSetup}>
                {children}
                <Modal
                    animationType="slide"
                    transparent
                    visible={!!modalContent}
                    onRequestClose={closeModalCallback}
                    supportedOrientations={[
                        'portrait',
                        'landscape',
                        'landscape-left',
                        'landscape-right'
                    ]}
                >
                    {modalContent}
                </Modal>
            </ConfirmationModalContext.Provider>
        );
    }
    return (
        <ConfirmationModalContext.Provider
            value={{
                setErrorModal: setErrorModalCallback,
                setProtectedContent: setProtectedContentCallback,
                modalContent,
                setModalContent: (newModalContent, hwBackHandler) => {
                    setCustomBackHandler(() => hwBackHandler);
                    setModalContent(newModalContent);
                }
            }}
        >
            {children}
            <View
                style={
                    modalContent
                        ? {
                              ...StyleSheet.absoluteFill,
                              height: '100%',
                              width: '100%'
                          }
                        : { height: 0, width: 0, display: 'none' }
                }
            >
                {modalContent}
            </View>
        </ConfirmationModalContext.Provider>
    );
};

AppModal.defaultProps = {
    children: []
};

AppModal.propTypes = {
    children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node])
};

export default AppModal;
